"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import Image from "next/image";
import { GraphsIcon } from "@/components/ui/icons/grpahs";
import { useQuizState } from "@/components/quiz/hooks/use-quiz-state";
import { Button } from "@workspace/ui/components/button";
import { ClockIcon } from "@/components/ui/icons/clock";
import { useQuizTouchpointImpression } from "@/components/quiz/hooks/use-quiz-touchpoint-impression";
import { type TouchpointType } from "@/lib/quiz-attribution";
import { QUIZ_VARIANT_TEST_B } from "@/lib/utils/common/constants";

const CDN_BASE = "https://cdn.inito.com/inito_website/home";
const inlineSectionMobileImage = `${CDN_BASE}/quiz-inline-section-desktop-v2.png`;
const inlineSectionDesktopImage = `${CDN_BASE}/quiz-inline-section-v2.png`;
const womenImage = `${CDN_BASE}/women.png`;

const ROTATE_INTERVAL_MS = 3500;

function RotatingText() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);

  const rotatingItems = useMemo(
    () => [
      {
        icon: <ClockIcon />,
        text: "Takes less than 3 minutes",
      },
      // {
      //   icon: (
      //     <Image
      //       src={womenImage}
      //       alt=""
      //       width={20}
      //       height={20}
      //       className="h-5 w-auto"
      //     />
      //   ),
      //   text: "15K+ women already took this quiz",
      // },
    ],
    [],
  );

  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     setIsFading(true);
  //     setTimeout(() => {
  //       setActiveIndex((prev) => (prev + 1) % rotatingItems.length);
  //       setIsFading(false);
  //     }, 300);
  //   }, ROTATE_INTERVAL_MS);

  //   return () => clearInterval(interval);
  // }, []);

  const currentItem = rotatingItems[activeIndex];

  return (
    <div className="flex h-5 items-center">
      <div
        className={`flex items-center gap-2 transition-opacity duration-300 ${
          isFading ? "opacity-0" : "opacity-100"
        }`}
      >
        {currentItem?.icon ?? null}
        <span
          id="quiz-inline-section-rotating-text"
          data-testid="quiz-inline-section-rotating-text"
          className="text-[14px] font-medium leading-5 tracking-[-0.3px] text-[#504C47]"
        >
          {currentItem?.text ?? ""}
        </span>
      </div>
    </div>
  );
}

export function QuizInlineSection({
  variant,
  touchpointType = "inline_section_home",
  alwaysShow = false,
}: {
  variant?: string | null;
  touchpointType?: TouchpointType;
  alwaysShow?: boolean;
}) {
  const { handleQuizTap, ctaLabel } = useQuizState();
  const sectionRef = useQuizTouchpointImpression<HTMLElement>(touchpointType, { fireEveryView: true });

  const handleStartQuiz = useCallback(() => {
    handleQuizTap(touchpointType);
  }, [handleQuizTap, touchpointType]);

  if (!alwaysShow && variant !== QUIZ_VARIANT_TEST_B) return null;

  return (
    <section
      ref={sectionRef}
      id="quiz-inline-section"
      className="bg-white pb-20 md:mb-0 md:px-20 md:py-[120px]"
    >
      <div className="mx-auto overflow-hidden md:bg-olive-1 md:flex md:max-w-[1280px] md:flex-row md:items-stretch md:justify-between md:rounded-[24px] md:p-6">
        {/* Mobile: image on top / Desktop: image on the right */}
        <div
          id="quiz-inline-image"
          className="relative w-full overflow-hidden md:order-2 md:w-[502px] md:shrink-0 md:rounded-[15px]"
        >
          <div className="relative w-full aspect-[430/459] md:h-full md:aspect-[502/430]">
            <Image
              src={inlineSectionMobileImage}
              alt=""
              fill
              className="object-cover md:hidden"
            />
            <Image
              src={inlineSectionDesktopImage}
              alt=""
              fill
              className="object-cover hidden md:block"
            />
          </div>
          {/* Desktop: GraphsIcon badge on image */}
          {/* <div className="absolute bottom-6 left-6 hidden items-center justify-center rounded-full bg-[#F3F4ED] ring-[6px] ring-white md:flex">
            <GraphsIcon className="size-[80px]" />
          </div> */}
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col md:order-1 self-start md:self-center">
          <div className="relative mt-6 flex flex-col gap-8 px-8 pb-8 md:mt-0 md:justify-center md:gap-8 md:p-6">
            {/* Mobile: overlapping badge */}
            {/* <div className="flex items-center justify-start md:hidden">
              <div className="flex items-center justify-center rounded-full bg-[#F3F4ED] ring-[7px] ring-white">
                <GraphsIcon className="size-[77px]" />
              </div>
            </div> */}

            {/* Text */}
            <div className="flex flex-col gap-4 md:gap-8">
              <h3
                id="quiz-inline-section-heading"
                data-testid="quiz-inline-section-heading"
                className="title-32 text-primary md:max-w-[470px] md:title-36"
              >
                {touchpointType === "inline_section_home"
                  ? "See how Inito fits into your Journey?"
                  : "Wondering how Inito fits into your fertility journey?"}
              </h3>
              <p
                id="quiz-inline-section-description"
                data-testid="quiz-inline-section-description"
                className="body-16 text-primary md:max-w-[481px] md:body-18"
              >
                {touchpointType === "inline_section_home"
                  ? "Take quiz to get a personalized sample Inito hormone chart and understand how Inito helps your unique fertility journey."
                  : "Get a personalized sample Inito hormone chart and see how Inito delivers detailed fertility insights tailored to your body."}
              </p>
            </div>

            {/* CTA + rotating text */}
            <div className="flex flex-col items-center gap-4 md:items-start">
              <Button
                variant="default"
                size="default"
                onClick={handleStartQuiz}
                id="quiz-inline-section-cta"
                data-testid="quiz-inline-section-cta"
                className="w-full md:w-[258px] md:rounded-[14px] md:px-10 md:py-[15px] md:title-18"
              >
                {ctaLabel}
              </Button>

              <RotatingText />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
